import { Button, Div, DivProps, Heading } from '@kizi/core-components';
import { IconV2 as Icon } from '@kizi/shared-components';
import React, { useEffect } from 'react';
import { Portal } from 'react-portal';
import { Mobile } from '../utils/breakpoint';

const cursorStyle = {
  cursor: 'pointer',
};

export interface MobileLayoverProps extends Omit<DivProps, 'title'> {
  animationDuration?: number;
  animationName?: string;
  bottomContent?: React.ReactNode;
  noHorizontalPadding?: boolean;
  onApply?: React.MouseEventHandler<HTMLButtonElement>;
  onClose: React.MouseEventHandler;
  show: boolean;
  title?: React.ReactNode;
  showIcon?: boolean;
  container?: React.ReactElement;
}

const useBodyScrollLock = (isLocked: boolean) => {
  useEffect(() => {
    if (isLocked) {
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = `-${scrollX}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        const body = document.body.style;
        body.position = '';
        body.top = '';
        body.left = '';
        body.width = '';
        body.height = '';
        body.overflow = '';

        window.scrollTo(scrollX, scrollY);
      };
    }

    return undefined;
  }, [isLocked]);
};

const Component: React.FC<MobileLayoverProps> = ({
  animationDuration = 0.3,
  animationName = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  bottomContent,
  noHorizontalPadding,
  onApply,
  onClose,
  show,
  showIcon = true,
  title,
  ...restProps
}: MobileLayoverProps) => {
  const animation = `${animationName} ${animationDuration}s`;

  useBodyScrollLock(show);

  let backgroundStyle = {
    opacity: show ? 0.6 : 0,
    zIndex: show ? 2 : -1,
    pointerEvents: show ? 'auto' : 'none',
  } as React.CSSProperties;

  let wrapperStyle = {
    transform: show ? 'translateY(0%)' : 'translateY(100%)',
    zIndex: show ? 99 : -1,
    willChange: 'transform',
    pointerEvents: show ? 'auto' : 'none',
  } as React.CSSProperties;

  return (
    <>
      <Div
        bg="#000"
        height="100vh"
        left={0}
        position="fixed"
        style={{
          ...backgroundStyle,
          transition: `opacity ${animation}`,
        }}
        top={0}
        width="100vw"
        overflow="hidden"
        onClick={onClose}
      />
      <Div
        bg="white"
        borderTopLeftRadius={2}
        borderTopRightRadius={2}
        bottom={0}
        display="flex"
        flexDirection="column"
        left={0}
        maxHeight="calc(100vh - 70px)"
        position="fixed"
        style={{
          ...wrapperStyle,
          transition: `transform ${animation}, opacity ${animation}`,
        }}
        width="100%"
      >
        {(title || showIcon) && (
          <Div
            display="grid"
            gridTemplateColumns="auto 17px"
            position="relative"
            mx={[4, null, 0]}
            my={[5, null, 0]}
            flexShrink={0}
          >
            {title && (
              <Div display="flex" mx="auto">
                {typeof title === 'string' ? (
                  <Heading as="h4" m={0} variant="H4">
                    {title}
                  </Heading>
                ) : (
                  title
                )}
              </Div>
            )}
            {showIcon && (
              <Icon
                onClick={onClose}
                fill="second.0"
                height={32}
                width={32}
                name="cross-in-circle"
                style={{
                  ...cursorStyle,
                  transform: 'translateY(-50%)',
                }}
              />
            )}
          </Div>
        )}
        <Div
          overflowY="auto"
          px={noHorizontalPadding ? 0 : 4}
          flexGrow={1}
          minHeight={0}
          {...restProps}
        />
        {bottomContent && (
          <Div
            bg="white"
            boxShadow="default"
            width="100%"
            zIndex={1}
            flexShrink={0}
          >
            <Div display="flex" p={3}>
              {typeof bottomContent === 'string' ? (
                <Button
                  onClick={(e) => {
                    onApply && onApply(e);
                    onClose(e);
                  }}
                  style={cursorStyle}
                  width="100%"
                  variant="primary"
                >
                  {bottomContent}
                </Button>
              ) : (
                bottomContent
              )}
            </Div>
          </Div>
        )}
      </Div>
    </>
  );
};

export const MobileLayover: React.FC<MobileLayoverProps> = ({
  container = <Mobile>children</Mobile>,
  ...props
}: MobileLayoverProps) => {
  return React.cloneElement(
    container,
    container.props,
    <Portal>
      <Component {...props} />
    </Portal>,
  );
};
